const MAP_LOAD_POLL_INTERVAL_SECONDS = 5;
const MAP_LOAD_MAX_ATTEMPTS = 60;

/**
 * Polls `loaded` every `intervalSeconds` up to `maxAttempts` times. Throws a descriptive
 * error if the map never finishes loading, so a stuck map fails the export job instead of
 * hanging `page.evaluate()` indefinitely.
 *
 * `getRenderError` reports a tile/source failure the map itself cannot: mapbox-gl counts an
 * errored source as loaded, so without this probe a tile outage resolves as a successful,
 * empty render.
 */
export async function pollForMapLoadComplete(
  loaded: () => boolean,
  wait: (seconds: number) => Promise<void>,
  getRenderError?: () => string | null,
): Promise<void> {
  for (let attempt = 0; attempt < MAP_LOAD_MAX_ATTEMPTS; attempt++) {
    await wait(MAP_LOAD_POLL_INTERVAL_SECONDS);

    const renderError = getRenderError?.();
    if (renderError) {
      throw new Error(`Map failed to render: ${renderError} (waitForMapLoadComplete)`);
    }

    if (loaded()) return;
  }
  throw new Error(
    `Map failed to load within ${MAP_LOAD_MAX_ATTEMPTS * MAP_LOAD_POLL_INTERVAL_SECONDS}s (waitForMapLoadComplete)`,
  );
}
